// src/pages/laboral/expedientes/FormCentroConciliacion.jsx
import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
} from "react";
import { notify } from "../../ui/notify";
import {
  Form,
  Input,
  Select,
  Upload,
  Typography,
  Row,
  Col,
  Alert,
  Button,
  Spin,
  Modal,
  ConfigProvider,
} from "antd";
import ScrollDatePicker from "../../calendar/ScrollDatePicker";
import { HappyProvider } from "@ant-design/happy-work-theme";

import {
  InboxOutlined,
  CalendarOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import FormEmpresas from "../empresas/FormEmpresas";
import { useDispatch, useSelector } from "react-redux";
import {
  actionConciliacionCreate,
  actionConciliacionUpdate,
} from "../../../redux/actions/conciliacion/conciliacion";
// ✅ NUEVO
import { actionAutoridadesGet } from "../../../redux/actions/autoridades/autoridades";
import useLaboralCatalogos from "../../../containers/pages/materias/laboral/useLaboralCatalogos";
import { actionEmpresasGet, actionEmpresaCreate } from "../../../redux/actions/empresas/empresas";
import { actionMedioNotificacionGet } from "../../../redux/actions/medio_notificacion/medio_notificacion";
import { actionAbogadosGet } from "../../../redux/actions/abogados/abogados";
import { actionObjetosGet } from "../../../redux/actions/objetos/objetos";
import { actionCatalogoConfigGet } from "../../../redux/actions/catalogos_config/catalogos_config";
import { actionIdentificacionCiudadGet } from "../../../redux/actions/identificacion_ciudades/identificacion_ciudades";
import {
  formaNotificacionLabel,
  origenNotificadoLabel,
} from "../../../utils/notificacionCatalogos";
import { usePermisos } from "../../../context/PermisosContext";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import SuccessOverlay from "../../feedback/SuccessOverlay";

const { Dragger } = Upload;
const { Text } = Typography;

/* ========= Utils ========= */
const collapseSpaces = (s) =>
  String(s ?? "")
    .normalize("NFC")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .replace(/\s+/g, " ");

const sanitizeByField = (name, v) => {
  const s = collapseSpaces(v);
  switch (name) {
    case "exp":
      return s.replace(/[^A-Za-z0-9_\-./ ]/g, "").slice(0, 50);
    case "abogado":
    case "abogado_contrario":
    case "trabajador_nombre":
      return s.replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ'.\- ]/g, "").slice(0, 100);
    case "empresa":
      return s
        .replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9&.,()\/\- ]/g, "")
        .slice(0, 120);
    default:
      return s;
  }
};

const toNullIfEmptyEffective = (v) => {
  if (v === undefined || v === null) return null;
  if (typeof v !== "string") return v;
  return v.replace(/\s/g, "") === "" ? null : v;
};

const makeNormalizer = (field) => (v) => {
  const clean = sanitizeByField(field, v);
  return toNullIfEmptyEffective(clean) === null ? "" : clean;
};

/* ========= Empresas / RS helpers ========= */
const pickCompanyName = (it) =>
  it?.nombre ??
  it?.nombre_cliente ??
  it?.empresa ??
  it?.name ??
  it?.razon_social ??
  "";

const pickCompanyId = (it) => it?.id_empresa ?? it?.id ?? null;

const buildRSOption = ({ label, value, empresa_id }) => ({
  label: collapseSpaces(label || ""),
  value: value ?? collapseSpaces(label || ""),
  empresa_id: empresa_id ?? null,
});

const normLabel = (txt) => collapseSpaces(txt || "").trim().toLowerCase();

const dedupRazones = (arr = []) => {
  const map = new Map();
  for (const r of arr) {
    const key = `${normLabel(r.label)}|${r.empresa_id ?? "null"}`;
    const existing = map.get(key);
    if (!existing) map.set(key, r);
    else if (existing.value == null && r.value != null) map.set(key, r);
  }
  return Array.from(map.values()).sort((a, b) =>
    a.label.localeCompare(b.label, "es")
  );
};

const buildEmpresasIndex = (items = []) => {
  const byName = new Map();
  if (!Array.isArray(items)) return byName;

  items.forEach((it) => {
    const name = collapseSpaces(pickCompanyName(it));
    if (!name) return;

    const idEmpresa = pickCompanyId(it);
    if (!byName.has(name)) byName.set(name, { empresaIds: new Set(), razones: [] });
    const bucket = byName.get(name);
    if (idEmpresa != null) bucket.empresaIds.add(idEmpresa);

    const rsArr = Array.isArray(it?.razones_sociales) ? it.razones_sociales : [];
    rsArr.forEach((rs) => {
      const label = rs?.nombre ?? rs?.razon_social ?? rs?.name ?? "";
      const value = rs?.id_empresa_razon_social ?? rs?.id ?? null;
      const empresa_id = rs?.id_empresa ?? idEmpresa ?? null;
      if (!label) return;
      bucket.razones.push(buildRSOption({ label, value, empresa_id }));
    });

    const plainRS =
      it?.razon_social && String(it.razon_social).trim() !== ""
        ? String(it.razon_social)
        : null;

    if (plainRS)
      bucket.razones.push(
        buildRSOption({ label: plainRS, value: null, empresa_id: idEmpresa })
      );
  });

  for (const [name, bucket] of byName.entries()) {
    bucket.razones = dedupRazones(bucket.razones);
    byName.set(name, bucket);
  }
  return byName;
};
const ALL = "__all__";

const coerceItems = (slice) => {
  if (!slice) return [];
  const data = slice.data ?? slice;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  return [];
};

const coerceEmpresasArray = (slice) => {
  if (Array.isArray(slice)) return slice;
  if (Array.isArray(slice?.items)) return slice.items;
  if (Array.isArray(slice?.data)) return slice.data;
  if (Array.isArray(slice?.list)) return slice.list;
  if (Array.isArray(slice?.items?.data)) return slice.items.data;
  if (Array.isArray(slice?.data?.items)) return slice.data.items;
  return [];
};

const coerceItemsFromSlice = (slice) => {
  if (!slice) return [];
  if (Array.isArray(slice.items)) return slice.items;
  if (Array.isArray(slice.data)) return slice.data;
  if (Array.isArray(slice?.data?.items)) return slice.data.items;
  if (Array.isArray(slice?.payload?.items)) return slice.payload.items;
  return [];
};

/* ========= Date helpers ========= */
// UI -> backend
const normalizeFechaDDMMYYYY_to_YYYYMMDD = (str) => {
  if (!str) return null;
  const digits = String(str).replace(/\D/g, "");
  if (digits.length !== 8) return null;
  const dd = digits.substring(0, 2);
  const mm = digits.substring(2, 4);
  const yyyy = digits.substring(4, 8);
  return `${yyyy}-${mm}-${dd}`;
};

const normalizeFechaHoraDDMMYYYYHHmm_to_YYYYMMDD_HHMM = (str) => {
  if (!str) return null;
  const digits = String(str).replace(/\D/g, "");
  if (digits.length !== 12) return null;
  const dd = digits.substring(0, 2);
  const mm = digits.substring(2, 4);
  const yyyy = digits.substring(4, 8);
  const HH = digits.substring(8, 10);
  const MM = digits.substring(10, 12);
  return `${yyyy}-${mm}-${dd} ${HH}:${MM}`;
};

const buildExpedienteFormat = (raw) => {
  if (raw === null || raw === "") return null;
  const s = String(raw).trim().toUpperCase();
  return s.replace(/\//g, "-");
};

// Detecta si initialValues ya trae data real (no {} / no placeholders)
const hasMeaningfulInitialValues = (iv) => {
  if (!iv || typeof iv !== "object") return false;
  const keys = Object.keys(iv);
  if (!keys.length) return false;

  const probes = [
    iv.exp,
    iv.fecha_emision_expediente,
    iv.fecha_audiencia,
    Array.isArray(iv.trabajadores) ? iv.trabajadores.join("") : "",
  ];

  return probes.some((x) => String(x ?? "").trim() !== "");
};

// Efecto "inset" (onda que crece desde el punto de clic) para todos los
// botones de este formulario, excepto Guardar que usa HappyProvider aparte.
const createWaveHolder = (node) => {
  const { borderWidth } = getComputedStyle(node);
  const borderWidthNum = Number.parseInt(borderWidth, 10) || 0;
  const div = document.createElement("div");
  div.style.position = "absolute";
  div.style.inset = `-${borderWidthNum}px`;
  div.style.borderRadius = "inherit";
  div.style.background = "transparent";
  div.style.zIndex = "999";
  div.style.pointerEvents = "none";
  div.style.overflow = "hidden";
  node.appendChild(div);
  return div;
};

const createWaveDot = (holder, color, left, top, size = 0) => {
  const dot = document.createElement("div");
  dot.style.position = "absolute";
  dot.style.insetInlineStart = `${left}px`;
  dot.style.top = `${top}px`;
  dot.style.width = `${size}px`;
  dot.style.height = `${size}px`;
  dot.style.borderRadius = "50%";
  dot.style.background = color;
  dot.style.transform = "translate3d(-50%, -50%, 0)";
  dot.style.transition = "all 1s ease-out";
  holder.appendChild(dot);
  return dot;
};

const showInsetEffect = (node, { event, component }) => {
  if (component !== "Button") return;
  const holder = createWaveHolder(node);
  const rect = holder.getBoundingClientRect();
  const left = event.clientX - rect.left;
  const top = event.clientY - rect.top;
  const dot = createWaveDot(holder, "rgba(10, 83, 154, 0.35)", left, top);
  requestAnimationFrame(() => {
    dot.ontransitionend = () => {
      holder.remove();
    };
    dot.style.width = "200px";
    dot.style.height = "200px";
    dot.style.opacity = "0";
  });
};

const FormCentroConciliacion = React.forwardRef(
  (
    {
      initialValues = {},
      onSaved,
      onCreated,
      idEstado,
      onCancel,
      idAutoridad,
      idCiudad,
      nombreIdentificacionCiudad,
      isEdit = false,
      idExpediente = null,
            showTopActions = true,

    },
    ref
  ) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [nuevaEmpresaModal, setNuevaEmpresaModal] = useState(false);
    const [savingNuevaEmpresa, setSavingNuevaEmpresa] = useState(false);
    const nuevaEmpresaRef = useRef(null);
    const [success, setSuccess] = useState({ show: false, title: "", subtitle: "" });
    const pendingAfterSuccessRef = useRef(null);
// =========================
// ✅ Estado/Ciudad/Autoridad (para crear y editar)
// - EDIT: si llega idCiudad/idAutoridad => se precarga
// - CREATE: arranca vacío y el usuario elige
// =========================
const autoridadesSlice = useSelector((state) => state?.autoridades ?? {});

// Ciudades permitidas al usuario para el módulo Centro de Conciliación
// (null = todas; ver columna "Ciudades" en Usuarios > Permisos).
const { allowedCities } = usePermisos() || { allowedCities: () => null };
const allowedCiudadesConciliacion = allowedCities("conciliacion");

// En CREATE arranca null (vacío). En EDIT usa props si llegan.
const [selectedEstadoId, setSelectedEstadoId] = useState(null);
const [selectedCiudadId, setSelectedCiudadId] = useState(null);
const [selectedAutoridadId, setSelectedAutoridadId] = useState(null);
const [selectedAutoridadData, setSelectedAutoridadData] = useState(null);

// catálogos estados/ciudades
const {
  estadosOptions,
  ciudadesOptions,
  estadosById,
  ciudadesById,
} = useLaboralCatalogos(selectedEstadoId ?? ALL, selectedCiudadId ?? ALL, {
  modulo: "conciliacion",
});

// cargar autoridades una vez
useEffect(() => {
  dispatch(actionAutoridadesGet({}));
}, [dispatch]);

const autoridadesItems = useMemo(() => coerceItems(autoridadesSlice), [autoridadesSlice]);

// Set de ciudades con autoridad (para filtrar). `autoridadesItems` ya viene
// filtrada por el permiso de ciudades del módulo desde el backend
// (/autoridades?modulo=conciliacion).
const ciudadIdsConAutoridad = useMemo(() => {
  const s = new Set();
  (autoridadesItems || []).forEach((au) => {
    const idc = au?.id_ciudad;
    if (idc === undefined || idc === null || String(idc) === "") return;
    s.add(Number(idc));
  });
  return s;
}, [autoridadesItems]);

// Set de estados con al menos 1 ciudad con autoridad
const estadoIdsConAutoridad = useMemo(() => {
  const s = new Set();
  ciudadIdsConAutoridad.forEach((idCiudadX) => {
    const c = ciudadesById?.[String(idCiudadX)] || ciudadesById?.[Number(idCiudadX)];
    const idEstado = c?.id_estado ?? c?.idEstado ?? null;
    if (idEstado !== undefined && idEstado !== null && String(idEstado) !== "") s.add(Number(idEstado));
  });
  return s;
}, [ciudadIdsConAutoridad, ciudadesById]);

const estadosOptionsFinal = useMemo(() => {
  const base = Array.isArray(estadosOptions) ? estadosOptions : [];
  const filtered = base.filter((o) => estadoIdsConAutoridad.has(Number(o.value)));
  return filtered;
}, [estadosOptions, estadoIdsConAutoridad]);

const ciudadesOptionsFinal = useMemo(() => {
  const base = Array.isArray(ciudadesOptions) ? ciudadesOptions : [];
  if (!selectedEstadoId) return [];
  const filtered = base.filter((o) => ciudadIdsConAutoridad.has(Number(o.value)));
  return filtered;
}, [ciudadesOptions, ciudadIdsConAutoridad, selectedEstadoId]);

// Catálogo de claves de identificación por ciudad (ej. UAC/CI), cargado
// una vez al montar. Permite mostrar la clave en cuanto se elige la
// CIUDAD, sin esperar a que también se elija la autoridad.
const [identificacionCiudadItems, setIdentificacionCiudadItems] = useState([]);

useEffect(() => {
  (async () => {
    try {
      const data = await dispatch(actionIdentificacionCiudadGet());
      setIdentificacionCiudadItems(Array.isArray(data?.items) ? data.items : []);
    } catch {
      // deja el catálogo vacío; el badge simplemente no se mostrará
    }
  })();
}, [dispatch]);

// Clave de identificación de ciudad a mostrar junto al folio.
// Prioridad: 1) la de la autoridad ya seleccionada (más precisa, viene
// del backend como `nombre_identificacion_ciudad`), 2) la que corresponda
// a la ciudad elegida en el catálogo, 3) el valor inicial recibido por
// props (precarga en edit/navegación).
const cityIdentificationCode = useMemo(() => {
  if (selectedAutoridadData?.nombre_identificacion_ciudad) {
    return selectedAutoridadData.nombre_identificacion_ciudad;
  }

  if (selectedCiudadId) {
    const match = (identificacionCiudadItems || []).find(
      (it) => Number(it?.id_ciudad) === Number(selectedCiudadId)
    );
    if (match?.nombre) return match.nombre;
  }

  return nombreIdentificacionCiudad || "";
}, [
  selectedAutoridadData,
  selectedCiudadId,
  identificacionCiudadItems,
  nombreIdentificacionCiudad,
]);

const autoridadOptions = useMemo(() => {
  const base = (autoridadesItems || [])
    .filter((au) => {
      if (!selectedCiudadId) return false; // si no hay ciudad, no muestres
      return Number(au?.id_ciudad || 0) === Number(selectedCiudadId);
    })
    .map((au) => ({
      label: collapseSpaces(au?.nombre || ""),
      value: au?.id,
      data: au,
    }))
    .filter((o) => o.label);

  return base;
}, [autoridadesItems, selectedCiudadId]);

// ✅ Precarga en EDIT (y también si vienes con props en CREATE)
useEffect(() => {
  // idCiudad/idAutoridad pueden venir por props (edit) o por navegación
  if (idCiudad != null && String(idCiudad).trim() !== "") {
    const idc = Number(idCiudad);
    setSelectedCiudadId(idc);

    const c = ciudadesById?.[String(idc)] || ciudadesById?.[Number(idc)];
    const idEstadoFromCiudad = c?.id_estado ?? c?.idEstado ?? null;
    if (idEstadoFromCiudad != null) setSelectedEstadoId(Number(idEstadoFromCiudad));
  } else if (!isEdit) {
    // CREATE sin props => vacío
    setSelectedEstadoId(null);
    setSelectedCiudadId(null);
    setSelectedAutoridadId(null);
    setSelectedAutoridadData(null);
  }
}, [idCiudad, ciudadesById, isEdit]);

useEffect(() => {
  if (idAutoridad != null && String(idAutoridad).trim() !== "") {
    const ida = Number(idAutoridad);
    setSelectedAutoridadId(ida);

    const found = (autoridadesItems || []).find((x) => Number(x?.id) === ida);
    setSelectedAutoridadData(found || null);
  } else if (!isEdit) {
    setSelectedAutoridadId(null);
    setSelectedAutoridadData(null);
  }
}, [idAutoridad, autoridadesItems, isEdit]);

const handleChangeEstado = (value) => {
  const v = value ?? null;
  setSelectedEstadoId(v);
  setSelectedCiudadId(null);
  setSelectedAutoridadId(null);
  setSelectedAutoridadData(null);
};

const handleChangeCiudad = (value) => {
  const v = value ?? null;
  setSelectedCiudadId(v);
  setSelectedAutoridadId(null);
  setSelectedAutoridadData(null);
};

    // Documentos (solo citatorio)
    const [documentsMap, setDocumentsMap] = useState({ citatorio: null });
    const [docErrors, setDocErrors] = useState({});
    const [hasExistingCitatorio, setHasExistingCitatorio] = useState(false);

    const [medioNotifOptions, setMedioNotifOptions] = useState([]);
    const [loadingMedios, setLoadingMedios] = useState(false);
    const [formaNotifOptions, setFormaNotifOptions] = useState([]);
    const [origenNotifOptions, setOrigenNotifOptions] = useState([]);

    const fechaEmisionSel = Form.useWatch("fecha_emision_expediente", form);

    const expedienteYear = useMemo(() => {
      if (!fechaEmisionSel) return dayjs().year();
      const parsed = dayjs(fechaEmisionSel, "DD/MM/YYYY", true);
      if (!parsed.isValid()) return dayjs().year();
      return parsed.year();
    }, [fechaEmisionSel]);

    const handleGuardarNuevaEmpresa = async (payload) => {
      setSavingNuevaEmpresa(true);
      try {
        await dispatch(actionEmpresaCreate(payload));
        await dispatch(actionEmpresasGet());
        notify.success({ message: "Empresa creada", description: "Ya puedes seleccionarla en Razón Social.", placement: "bottomRight" });
        setNuevaEmpresaModal(false);
      } catch (e) {
        notify.error({
          message: "Error al crear empresa",
          description: e?.response?.data?.detail || e?.message || "Intenta de nuevo.",
          placement: "bottomRight",
        });
      } finally {
        setSavingNuevaEmpresa(false);
      }
    };

    /* ==== Cargar catálogos al montar ==== */
    useEffect(() => {
      dispatch(actionEmpresasGet());
      dispatch(actionAbogadosGet());
      dispatch(actionObjetosGet());

      (async () => {
        try {
          const data = await dispatch(actionCatalogoConfigGet("forma_notificacion"));
          const items = Array.isArray(data?.items) ? data.items : [];
          setFormaNotifOptions(
            items.map((i) => ({
              label: formaNotificacionLabel(i.nombre),
              value: i.nombre,
            }))
          );
        } catch {
          // deja las opciones vacías; el select simplemente no tendrá nada que mostrar
        }
      })();

      (async () => {
        try {
          const data = await dispatch(actionCatalogoConfigGet("origen_notificado"));
          const items = Array.isArray(data?.items) ? data.items : [];
          setOrigenNotifOptions(
            items.map((i) => ({
              label: origenNotificadoLabel(i.nombre),
              value: i.nombre,
            }))
          );
        } catch {
          // deja las opciones vacías
        }
      })();

      (async () => {
        try {
          setLoadingMedios(true);
          const result = await dispatch(actionMedioNotificacionGet());

          let items = [];
          if (Array.isArray(result)) items = result;
          else if (result && Array.isArray(result.items)) items = result.items;
          else {
            notify.error({
              message: "Error al cargar medios de notificación",
              description:
                typeof result === "string"
                  ? result
                  : "Respuesta inesperada del servidor",
              placement: "bottomRight",
            });
            return;
          }

          const opts = items
            .filter((m) => m && m.id != null)
            .map((m) => ({
              label: m.nombre || m.descripcion || `Medio ${m.id}`,
              value: m.id,
            }));

          setMedioNotifOptions(opts);
        } catch (e) {
          notify.error({
            message: "Error inesperado al cargar medios de notificación",
            description: e?.message || "Sin detalle",
            placement: "bottomRight",
          });
        } finally {
          setLoadingMedios(false);
        }
      })();
    }, [dispatch]);

    // Slices
    const empresasSlice = useSelector((state) => state?.empresas ?? {});
    const abogadosSlice = useSelector((state) => state.abogados || {});
    const objetosSlice = useSelector((state) => state.objetos || {});

    // Items
    const empresasItems = useMemo(
      () => coerceEmpresasArray(empresasSlice),
      [empresasSlice]
    );
    const empresasIndex = useMemo(
      () => buildEmpresasIndex(empresasItems),
      [empresasItems]
    );

    const abogadosItems = useMemo(
      () => coerceItemsFromSlice(abogadosSlice),
      [abogadosSlice]
    );
    const objetosItems = useMemo(
      () => coerceItemsFromSlice(objetosSlice),
      [objetosSlice]
    );

    const abogadoOptionsBase = useMemo(
      () =>
        abogadosItems
          .map((a) => ({
            label: collapseSpaces(a?.nombre_abogado || ""),
            value: collapseSpaces(a?.nombre_abogado || ""),
            _id: a?.id,
          }))
          .filter((o) => o.label),
      [abogadosItems]
    );

    const objetosOptions = useMemo(
      () =>
        objetosItems
          .map((a) => ({
            label: collapseSpaces(a?.nombre || ""),
            value: collapseSpaces(a?.nombre || ""),
            _id: a?.id,
          }))
          .filter((o) => o.label),
      [objetosItems]
    );

    // === Estados para selects con creación dinámica ===
    const [abogadoCustomNames, setAbogadoCustomNames] = useState([]);
    const [abogadoNewName, setAbogadoNewName] = useState("");

    const abogadoInputRef = useRef(null);

    // ==== Refs para navegación con Enter (como Tab) ====
    const expRef = useRef(null);
    const fechaEmisionRef = useRef(null);
    const fechaAudienciaRef = useRef(null);
    const razonSocialRef = useRef(null);

    const trabajadorRefs = useRef([]);
    const [trabajadoresCount, setTrabajadoresCount] = useState(1);
    const trabajadoresWatch = Form.useWatch("trabajadores", form);

    useEffect(() => {
      const arr = Array.isArray(trabajadoresWatch) ? trabajadoresWatch : [];
      const n = arr.length || 1;
      if (trabajadoresCount !== n) setTrabajadoresCount(n);
    }, [trabajadoresWatch]);

    const objetoSolicitudRef = useRef(null);
    const abogadoRef = useRef(null);

    const formaNotifRef = useRef(null);
    const origenActuarioRef = useRef(null);
    const fechaRecepcionRef = useRef(null);
    const medioNotifRef = useRef(null);

    const focusOrder = [
      fechaEmisionRef,
      razonSocialRef,
      objetoSolicitudRef,
      expRef,
      fechaAudienciaRef,
      abogadoRef,
      formaNotifRef,
      origenActuarioRef,
      fechaRecepcionRef,
      medioNotifRef,
    ];

    const focusNext = (currentRef) => {
      const idx = focusOrder.indexOf(currentRef);
      if (idx === -1) return;
      for (let i = idx + 1; i < focusOrder.length; i++) {
        const r = focusOrder[i];
        if (r && r.current && typeof r.current.focus === "function") {
          r.current.focus();
          return;
        }
      }
    };

    const makeEnterHandler = (fieldRef) => (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        focusNext(fieldRef);
      }
    };

    const formaNotifSel = Form.useWatch("forma_notificacion", form);

    // Todas las razones sociales de todas las empresas
    const allRazonesSociales = useMemo(() => {
      const all = [];
      for (const [, bucket] of empresasIndex.entries()) {
        all.push(...bucket.razones);
      }
      return dedupRazones(all);
    }, [empresasIndex]);

    // Abogados base + creados
    const abogadoOptions = useMemo(() => {
      const extra = abogadoCustomNames.map((n) => ({ label: n, value: n }));
      return [...abogadoOptionsBase, ...extra];
    }, [abogadoOptionsBase, abogadoCustomNames]);

    /* ======================= Upload Documento ======================= */
    const beforeUploadDoc = (key) => (file) => {
      setDocErrors((prev) => ({ ...prev, [key]: "" }));
      const isOkType =
        file.type === "application/pdf" ||
        file.type === "image/png" ||
        file.type === "image/jpeg" ||
        /\.pdf$/i.test(file.name) ||
        /\.(png|jpe?g)$/i.test(file.name);

      if (!isOkType) {
        const msg = "Solo PDF/JPG/PNG.";
        setDocErrors((prev) => ({ ...prev, [key]: msg }));
        notify.error({ message: msg, placement: "bottomRight" });
        return Upload.LIST_IGNORE;
      }

      const maxMB = 25;
      if (file.size > maxMB * 1024 * 1024) {
        const msg = `El archivo supera ${maxMB} MB.`;
        setDocErrors((prev) => ({ ...prev, [key]: msg }));
        notify.error({ message: msg, placement: "bottomRight" });
        return Upload.LIST_IGNORE;
      }

      setDocumentsMap((prev) => ({ ...prev, [key]: file }));
      return false;
    };

    const removeDoc = (key) => {
      setDocumentsMap((prev) => ({ ...prev, [key]: null }));
      setDocErrors((prev) => ({ ...prev, [key]: "" }));
    };

    /* ======================= Validaciones ======================= */
    const rules = {
  exp: [
    {
      validator: (_, v) => {
        if (!v) return Promise.resolve();
        const ok = /^[A-Za-z0-9_.\/\- ]{1,50}$/.test(v);
        return ok
          ? Promise.resolve()
          : Promise.reject(new Error("Solo letras, números y _ . / - y espacios."));
      },
    },
  ],

  empresa_razon_social_id: [],
  fecha_audiencia: [{ required: true, message: "Selecciona fecha y hora" }],

  fecha_emision_expediente: [],
  fecha_hora_cita_recepcion: [],
  objeto_solicitud: [],
  forma_notificacion: [],
  origen_actuario: [],
  id_medio_notificacion: [],
};

    /* ======================= Handlers creación dinámica ======================= */
    const handleAddAbogado = (e) => {
      e.preventDefault();
      const raw = (abogadoNewName || "").trim();
      if (!raw) return;
      const clean = raw.replace(/_/g, "");
      if (!clean) return;

      const existsBase = abogadoOptionsBase.some(
        (o) => o.label.toLowerCase() === clean.toLowerCase()
      );
      const existsExtra = abogadoCustomNames.some(
        (n) => n.toLowerCase() === clean.toLowerCase()
      );
      if (existsBase || existsExtra) {
        setAbogadoNewName("");
        return;
      }

      setAbogadoCustomNames((prev) => [...prev, clean]);
      setAbogadoNewName("");
      form.setFieldsValue({ abogado: clean });

      setTimeout(() => {
        abogadoInputRef.current?.focus();
      }, 0);
    };


    /* ======================= Inputs con calendario ======================= */
    const [openFechaEmisionPicker, setOpenFechaEmisionPicker] = useState(false);
    const [openFechaAudienciaPicker, setOpenFechaAudienciaPicker] = useState(false);
    const [openFechaRecepcionPicker, setOpenFechaRecepcionPicker] = useState(false);

    const fechaEmisionValue = Form.useWatch("fecha_emision_expediente", form);
    const fechaAudienciaValue = Form.useWatch("fecha_audiencia", form);
    const fechaRecepcionValue = Form.useWatch("fecha_hora_cita_recepcion", form);

    const makeFechaInputHandler = (fieldName) => (e) => {
      let v = e.target.value.replace(/\D/g, "");
      let formatted = v;
      if (v.length > 2) formatted = v.slice(0, 2) + "/" + v.slice(2);
      if (v.length > 4) formatted = formatted.slice(0, 5) + "/" + formatted.slice(5);
      formatted = formatted.slice(0, 10);
      e.target.value = formatted;
      form.setFieldsValue({ [fieldName]: formatted });
    };

    const makeFechaHoraInputHandler = (fieldName) => (e) => {
      let v = e.target.value.replace(/\D/g, "");
      let formatted = v;
      if (v.length > 2) formatted = v.slice(0, 2) + "/" + v.slice(2);
      if (v.length > 4) formatted = formatted.slice(0, 5) + "/" + formatted.slice(5);
      if (v.length > 8) formatted = formatted.slice(0, 10) + " " + formatted.slice(10);
      if (v.length > 10) formatted = formatted.slice(0, 13) + ":" + formatted.slice(13);
      formatted = formatted.slice(0, 16);
      e.target.value = formatted;
      form.setFieldsValue({ [fieldName]: formatted });
    };

    const handleFechaEmisionCalendarChange = (value) => {
      const formatted = value ? value.format("DD/MM/YYYY") : "";
      form.setFieldsValue({ fecha_emision_expediente: formatted });
    };

    const handleFechaAudienciaCalendarChange = (value) => {
      const formatted = value ? value.format("DD/MM/YYYY HH:mm") : "";
      form.setFieldsValue({ fecha_audiencia: formatted });
    };

    const handleFechaRecepcionCalendarChange = (value) => {
      const formatted = value ? value.format("DD/MM/YYYY HH:mm") : "";
      form.setFieldsValue({ fecha_hora_cita_recepcion: formatted });
    };

    /* ======================= Inicial (CORREGIDO: NO PISA MIENTRAS ESCRIBES) ======================= */
    const didInitRef = useRef(false);
    const lastAppliedKeyRef = useRef(null);

    useEffect(() => {
      // CREATE: inicializa 1 sola vez
      if (!isEdit) {
        if (didInitRef.current) return;

        const baseInit = {
          abogado_contrario: "",
          trabajadores: [""],
          forma_notificacion: undefined,
          origen_actuario: undefined,
          id_medio_notificacion: undefined,
          fecha_hora_cita_recepcion: undefined,
          ...initialValues,
        };

        form.setFieldsValue(baseInit);

        setDocumentsMap({ citatorio: null });
        setDocErrors({});
        setHasExistingCitatorio(false);

        didInitRef.current = true;
        lastAppliedKeyRef.current = "CREATE";
        return;
      }

      // EDIT: solo aplica cuando initialValues ya tiene datos reales
      const editKey = String(idExpediente ?? "");
      const hasData = hasMeaningfulInitialValues(initialValues);

      if (!editKey) return;
      if (!hasData) return;

      // evita re-aplicar sobre el mismo expediente
      if (lastAppliedKeyRef.current === editKey && didInitRef.current) return;

      const baseInit = {
        abogado_contrario: "",
        trabajadores: [""],
        forma_notificacion: undefined,
        origen_actuario: undefined,
        id_medio_notificacion: undefined,
        fecha_hora_cita_recepcion: undefined,
        ...initialValues,
      };

      form.setFieldsValue(baseInit);

      setDocErrors({});
      setHasExistingCitatorio(false);

      didInitRef.current = true;
      lastAppliedKeyRef.current = editKey;
    }, [initialValues, form, isEdit, idExpediente]);

    /* ======================= Guardado (create / update) ======================= */
    const buildAndSend = async (values) => {
      if (!selectedEstadoId || !selectedCiudadId || !selectedAutoridadId) {
        notify.error({
          message: "Faltan selecciones",
          description: "Selecciona Estado, Ciudad y Autoridad.",
          placement: "bottomRight",
        });
        return;
      }

      if (
        allowedCiudadesConciliacion !== null &&
        !allowedCiudadesConciliacion.includes(Number(selectedCiudadId))
      ) {
        notify.error({
          message: "Ciudad no permitida",
          description: "No tienes permiso para registrar expedientes en esta ciudad.",
          placement: "bottomRight",
        });
        return;
      }

      const trabajadoresLimpios = (Array.isArray(values.trabajadores) ? values.trabajadores : [])
        .map((n) => sanitizeByField("trabajador_nombre", n))
        .filter((n) => toNullIfEmptyEffective(n) !== null);

      const folioEscrito = sanitizeByField("exp", values.exp);
      // Si la ciudad seleccionada tiene una clave de identificación
      // registrada (ej. UAC/CI), se antepone al folio escrito por el
      // usuario. Si la ciudad no tiene clave registrada, se manda el
      // folio tal cual (sin anteponer nada).
      const expConCodigo =
        cityIdentificationCode && folioEscrito
          ? `${cityIdentificationCode}/${folioEscrito}`
          : folioEscrito;

      const clean = {
        exp: sanitizeByField("exp", expConCodigo),
        abogado: sanitizeByField("abogado", values.abogado),
        abogado_contrario: sanitizeByField("abogado_contrario", values.abogado_contrario),

        forma_notificacion: values.forma_notificacion || null,
        origen_actuario: values.origen_actuario || null,

        fecha_emision_expediente: normalizeFechaDDMMYYYY_to_YYYYMMDD(values.fecha_emision_expediente),
        fecha_audiencia: normalizeFechaHoraDDMMYYYYHHmm_to_YYYYMMDD_HHMM(values.fecha_audiencia),
        fecha_hora_cita_recepcion: normalizeFechaHoraDDMMYYYYHHmm_to_YYYYMMDD_HHMM(
          values.fecha_hora_cita_recepcion
        ),

        id_medio_notificacion: values.id_medio_notificacion
          ? Number(values.id_medio_notificacion)
          : null,
      };

      const id_abogado =
        abogadoOptionsBase.find((a) => a.value === clean.abogado)?._id ?? null;

      const objetoNombre = values.objeto_solicitud || null;
      const objetoMatch = objetosOptions.find((o) => o.value === objetoNombre);
      const id_objeto = objetoMatch ? objetoMatch._id : null;

      const id_autoridad = Number(selectedAutoridadId);
      const id_ciudad = Number(selectedCiudadId);

      const esAbogadoNuevo = abogadoCustomNames.some(
        (n) => n.toLowerCase() === (clean.abogado || "").toLowerCase()
      );

      // IDs numéricos de razones sociales seleccionadas (solo para referencia en agenda)
      const rsRaw = values.empresa_razon_social_id ?? [];
      const rsArr = Array.isArray(rsRaw) ? rsRaw : [rsRaw];
      const razones_sociales_ids = rsArr
        .map((v) => (typeof v === "object" ? v.value ?? null : v))
        .filter((v) => v !== null && v !== undefined && /^\d+$/.test(String(v).trim()))
        .map(Number);

      const requiredErrors = [];
      if (!clean.fecha_audiencia) requiredErrors.push("fecha_audiencia");
      if (trabajadoresLimpios.length === 0) requiredErrors.push("trabajadores");

      if (requiredErrors.length > 0) {
        notify.error({
          message: "Faltan campos obligatorios: " + requiredErrors.join(", "),
          placement: "bottomRight",
        });
        return;
      }

      const backendPayload = {
        expediente: clean.exp,

        fecha_emision_expediente: clean.fecha_emision_expediente,
        fecha_audiencia: clean.fecha_audiencia,

        id_abogado: id_abogado ?? null,
        abogado_nuevo_nombre: esAbogadoNuevo ? clean.abogado : null,

        abogado_contrario: toNullIfEmptyEffective(clean.abogado_contrario) ?? "",
        trabajadores: trabajadoresLimpios,

        id_autoridad: id_autoridad,
        id_ciudad: id_ciudad,
        id_objeto: id_objeto,

        forma_notificacion: clean.forma_notificacion,
        origen_actuario: clean.origen_actuario,
        fecha_hora_cita_recepcion: clean.fecha_hora_cita_recepcion,
        id_medio_notificacion: clean.id_medio_notificacion,

        razones_sociales_ids,
      };

      const filesMap = {};
      if (documentsMap.citatorio) filesMap.citatorio = documentsMap.citatorio;

    try {
  setSubmitting(true);

  let result = null;

  if (isEdit) {
    if (!idExpediente) {
      notify.error({
        message: "Falta idExpediente para editar (no se puede actualizar).",
        placement: "bottomRight",
      });
      return;
    }

    result = await dispatch(
      actionConciliacionUpdate(
        idExpediente,
        backendPayload,
        () => {},
        filesMap
      )
    );
  } else {
    result = await dispatch(
      actionConciliacionCreate(
        backendPayload,
        () => {},
        filesMap
      )
    );
  }

  const expedienteId = result?.id;

  pendingAfterSuccessRef.current = () => {
    if (expedienteId) {
      if (onCreated) {
        onCreated(expedienteId);
      } else {
        navigate(`/materias/laboral/centro-conciliacion/${expedienteId}`);
      }
    } else {
      onSaved?.();
    }
  };

  setSuccess({
    show: true,
    title: isEdit ? "¡Cambios guardados!" : "¡Expediente creado!",
    subtitle: isEdit
      ? "Los cambios se guardaron correctamente."
      : "El expediente se guardó correctamente.",
  });
} finally {
  setSubmitting(false);
}
    };

    const handleSuccessDone = () => {
      setSuccess({ show: false, title: "", subtitle: "" });
      const action = pendingAfterSuccessRef.current;
      pendingAfterSuccessRef.current = null;
      action?.();
    };

    const handleSaveClick = async () => {
      try {
        const values = await form.validateFields();

      

        await buildAndSend(values);
      } catch (err) {
        const errorFields = err?.errorFields || [];
        if (errorFields.length) {
          notify.error({
            message: "Faltan campos obligatorios.",
            placement: "bottomRight",
            duration: 3,
          });
          form.scrollToField(errorFields[0].name, { block: "center" });
          return;
        }

       
      }
    };

    useImperativeHandle(ref, () => ({
      submit: () => handleSaveClick(),
      validate: () => form.validateFields(),
      getValues: () => form.getFieldsValue(true),
      focusFirst: () => {
        if (expRef.current && typeof expRef.current.focus === "function") expRef.current.focus();
      },
    }));

    const DocBlock = ({ keyName }) => (
      <div className="expediente-doc-block">
        {isEdit && hasExistingCitatorio && !documentsMap[keyName] ? (
          <div style={{ marginTop: 8 }}>
            <Alert
              type="info"
              showIcon
              message="Ya existe un citatorio guardado. Si subes uno nuevo, se reemplazará."
            />
          </div>
        ) : null}

        <Dragger
          multiple={false}
          maxCount={1}
          accept=".pdf,.png,.jpg,.jpeg"
          beforeUpload={beforeUploadDoc(keyName)}
          onRemove={() => {
            removeDoc(keyName);
            return true;
          }}
          fileList={
            documentsMap[keyName]
              ? [
                  {
                    uid: `-${keyName}`,
                    name: documentsMap[keyName].name,
                    status: "done",
                  },
                ]
              : []
          }
          style={{ marginTop: 8, minHeight: 260, borderRadius: 8 }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ fontSize: 32, color: "#94a3b8" }} />
          </p>
          <p className="ant-upload-text" style={{ fontWeight: 600, fontSize: 13 }}>
            Haz clic o arrastra aquí el archivo
          </p>
          <p className="ant-upload-hint" style={{ fontSize: 11, marginBottom: 8 }}>
            PDF, JPG o PNG · máx. 25MB · se enviará al presionar{" "}
            {isEdit ? '"Guardar cambios"' : '"Guardar expediente"'}.
          </p>
          <Button style={{ fontWeight: 700, letterSpacing: "0.05em" }}>
            SELECCIONAR ARCHIVO
          </Button>
        </Dragger>

        {docErrors[keyName] ? (
          <Alert
            style={{ marginTop: 8 }}
            type="error"
            showIcon
            message={docErrors[keyName]}
          />
        ) : null}
      </div>
    );
// ✅ CREATE: seleccionar por default el primer ESTADO
useEffect(() => {
  if (isEdit) return;

  // si ya viene por props o el usuario ya eligió, no pises
  if (idCiudad != null || idAutoridad != null) return;
  if (selectedEstadoId != null) return;

  const first = Array.isArray(estadosOptionsFinal) ? estadosOptionsFinal[0] : null;
  if (!first?.value) return;

  setSelectedEstadoId(first.value);
}, [isEdit, idCiudad, idAutoridad, selectedEstadoId, estadosOptionsFinal]);

// ✅ CREATE: seleccionar por default la primera CIUDAD del estado
useEffect(() => {
  if (isEdit) return;
  if (idCiudad != null || idAutoridad != null) return;

  if (!selectedEstadoId) return;
  if (selectedCiudadId != null) return;

  const first = Array.isArray(ciudadesOptionsFinal) ? ciudadesOptionsFinal[0] : null;
  if (!first?.value) return;

  setSelectedCiudadId(first.value);
}, [isEdit, idCiudad, idAutoridad, selectedEstadoId, selectedCiudadId, ciudadesOptionsFinal]);
// ✅ CREATE: seleccionar por default la primera AUTORIDAD de la ciudad
useEffect(() => {
  if (isEdit) return;
  if (idCiudad != null || idAutoridad != null) return;

  if (!selectedCiudadId) return;
  if (selectedAutoridadId != null) return;

  const first = Array.isArray(autoridadOptions) ? autoridadOptions[0] : null;
  if (!first?.value) return;

  setSelectedAutoridadId(first.value);
  setSelectedAutoridadData(first.data || null);
}, [isEdit, idCiudad, idAutoridad, selectedCiudadId, selectedAutoridadId, autoridadOptions]);

    return (
      <ConfigProvider wave={{ showEffect: showInsetEffect }}>
      <>
        <div className="expediente-form-wrapper">
          <section className="cc-page-header-card">
            <div className="expediente-form-header-left">
              <div className="expediente-form-title">
                {isEdit
                  ? "Editando Expediente Prejudicial"
                  : "Creando Expediente Prejudicial"}
              </div>
              <div className="expediente-form-subtitle">
                Centro de Conciliación Laboral
              </div>
            </div>

            {showTopActions ? (
              <div style={{ display: "flex", gap: 8 }}>
                {onCancel ? (
                  <Button
                    onClick={onCancel}
                    disabled={submitting || success.show}
                    className="expediente-btn-clean"
                  >
                    Cancelar
                  </Button>
                ) : null}
                <HappyProvider>
                  <Button
                    type="primary"
                    onClick={handleSaveClick}
                    loading={submitting}
                    disabled={success.show}
                    className="expediente-btn-save"
                    icon={
                      <svg viewBox="0 0 448 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                        <path d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V154.5c0-17-6.7-33.3-18.7-45.3L400 60.7C388 48.7 371.7 42 354.5 42H64zm0 96c0-17.7 14.3-32 32-32H288c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V96zM224 288a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
                      </svg>
                    }
                  >
                    {isEdit ? "Guardar cambios" : "Guardar expediente"}
                  </Button>
                </HappyProvider>
              </div>
            ) : null}
          </section>

          <Spin
            spinning={submitting}
            tip={isEdit ? "Guardando cambios..." : "Guardando expediente..."}
          >
            <Form form={form} layout="vertical" className="expediente-ant-form" autoComplete="off">

{/* Estado / Ciudad / Autoridad */}
<div className="cc-section-card cc-full-width">
  <div className="cc-section-header">
    <span className="cc-section-icon">
      <svg viewBox="0 0 384 512" width="14" height="14" fill="currentColor" aria-hidden="true">
        <path d="M168.3 499.2C116.1 435 0 279.4 0 192C0 85.96 85.96 0 192 0C298 0 384 85.96 384 192C384 279.4 267 435 215.7 499.2C203.4 514.5 180.6 514.5 168.3 499.2H168.3zM192 256C227.3 256 256 227.3 256 192C256 156.7 227.3 128 192 128C156.7 128 128 156.7 128 192C128 227.3 156.7 256 192 256z" />
      </svg>
    </span>
    Ubicación
  </div>
  <Row gutter={[12, 12]}>

  <Col xs={24} md={8}>
    <Form.Item label="ESTADO" required>
      <Select
        placeholder="Selecciona un estado"
        options={estadosOptionsFinal}
        value={selectedEstadoId}
        onChange={handleChangeEstado}
        showSearch
        optionFilterProp="label"
        allowClear
        getPopupContainer={(trigger) => trigger.parentNode}
      />
    </Form.Item>
  </Col>

  <Col xs={24} md={8}>
    <Form.Item label="CIUDAD" required>
      <Select
        placeholder={!selectedEstadoId ? "Selecciona un estado primero" : "Selecciona una ciudad"}
        options={ciudadesOptionsFinal}
        value={selectedCiudadId}
        onChange={handleChangeCiudad}
        disabled={!selectedEstadoId}
        showSearch
        optionFilterProp="label"
        allowClear
        getPopupContainer={(trigger) => trigger.parentNode}
      />
    </Form.Item>
  </Col>

  <Col xs={24} md={8}>
    <Form.Item label="AUTORIDAD" required>
      <Select
        placeholder={!selectedCiudadId ? "Selecciona una ciudad primero" : "Selecciona una autoridad"}
        options={autoridadOptions}
        value={selectedAutoridadId}
        onChange={(value, option) => {
          const v = value ?? null;
          setSelectedAutoridadId(v);
          setSelectedAutoridadData(option?.data || null);
        }}
        disabled={!selectedCiudadId}
        showSearch
        optionFilterProp="label"
        allowClear
        getPopupContainer={(trigger) => trigger.parentNode}
      />
    </Form.Item>
  </Col>
</Row>
</div>

                <Form.Item name="empresa_id" hidden>
                  <Input />
                </Form.Item>

                <div className="cc-body-grid">
                  {/* ===== Trabajadores (columna angosta) ===== */}
                  <div className="cc-section-card">
                    <div className="cc-section-header">
                      <span className="cc-section-icon">
                        <svg viewBox="0 0 640 512" width="14" height="14" fill="currentColor" aria-hidden="true">
                          <path d="M319.9 320c57.41 0 103.1-46.56 103.1-104c0-57.44-46.54-104-103.1-104c-57.41 0-103.1 46.56-103.1 104C215.9 273.4 262.5 320 319.9 320zM369.9 352H270.1C191.6 352 128 411.7 128 485.3C128 500.1 140.7 512 156.4 512h327.2C499.3 512 512 500.1 512 485.3C512 411.7 448.4 352 369.9 352zM512 160c44.18 0 80-35.82 80-80S556.2 0 512 0c-44.18 0-80 35.82-80 80S467.8 160 512 160zM183.9 216c0-5.449 .9824-10.63 1.609-15.91C174.6 194.1 162.6 192 149.9 192H88.08C39.44 192 0 233.8 0 285.3C0 295.6 7.887 304 17.62 304h199.5C196.7 280.2 183.9 249.7 183.9 216zM128 160c44.18 0 80-35.82 80-80S172.2 0 128 0C83.82 0 48 35.82 48 80S83.82 160 128 160zM551.9 192h-61.84c-12.8 0-24.88 3.037-35.86 8.24C454.8 205.5 455.8 210.6 455.8 216c0 33.71-12.78 64.21-33.16 88h199.7C632.1 304 640 295.6 640 285.3C640 233.8 600.6 192 551.9 192z" />
                        </svg>
                      </span>
                      Trabajadores
                      <span className="cc-count-badge">{trabajadoresCount}</span>
                    </div>

                    <Form.List
                      name="trabajadores"
                      rules={[
                        {
                          validator: async (_, trabajadores) => {
                            const hasValid = (trabajadores || []).some(
                              (n) =>
                                toNullIfEmptyEffective(
                                  sanitizeByField("trabajador_nombre", n)
                                ) !== null
                            );
                            if (!hasValid) {
                              return Promise.reject(
                                new Error("Agrega al menos un trabajador")
                              );
                            }
                          },
                        },
                      ]}
                    >
                      {(fields, { add, remove }, { errors }) => (
                        <>
                          <div className="trabajadores-scroll">
                            {fields.map((field, idx) => (
                              <Row key={field.key} gutter={[8, 8]} align="middle">
                                <Col flex="auto">
                                  <Form.Item
                                    {...field}
                                    style={{ marginBottom: 8 }}
                                    normalize={makeNormalizer("trabajador_nombre")}
                                    rules={[
                                      {
                                        validator: (_, v) => {
                                          const clean = sanitizeByField("trabajador_nombre", v);
                                          if (!clean) return Promise.resolve();
                                          const ok =
                                            /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ'.\- ]{1,100}$/.test(clean);
                                          return ok
                                            ? Promise.resolve()
                                            : Promise.reject(
                                                new Error("Solo letras, espacios y ' . -")
                                              );
                                        },
                                      },
                                    ]}
                                  >
                                    <Input
                                      ref={(el) => {
                                        trabajadorRefs.current[idx] = el;
                                      }}
                                      placeholder={`Trabajador ${idx + 1} (nombre completo)`}
                                      allowClear
                                      onKeyDown={(e) => {
                                        if (e.key !== "Enter") return;
                                        e.preventDefault();
                                        const isLast = idx === fields.length - 1;
                                        if (!isLast) {
                                          trabajadorRefs.current[idx + 1]?.focus();
                                          return;
                                        }
                                        const currentVal = form.getFieldValue([
                                          "trabajadores",
                                          idx,
                                        ]);
                                        if (String(currentVal || "").trim() !== "") {
                                          add("");
                                          setTimeout(
                                            () => trabajadorRefs.current[idx + 1]?.focus(),
                                            0
                                          );
                                        } else {
                                          fechaEmisionRef.current?.focus();
                                        }
                                      }}
                                    />
                                  </Form.Item>
                                </Col>

                                <Col>
                                  <Button
                                    danger
                                    onClick={() => remove(field.name)}
                                    disabled={fields.length <= 1}
                                    style={{ marginBottom: 8 }}
                                  >
                                    Quitar
                                  </Button>
                                </Col>
                              </Row>
                            ))}
                          </div>

                          <Button
                            icon={<PlusOutlined />}
                            onClick={() => {
                              const nextIdx = fields.length;
                              add("");
                              setTimeout(() => trabajadorRefs.current[nextIdx]?.focus(), 0);
                            }}
                            style={{ marginTop: 8, width: "100%" }}
                          >
                            Agregar trabajador
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </div>

                  {/* ===== Datos del expediente ===== */}
                  <div className="cc-section-card">
                    <div className="cc-section-header">
                      <span className="cc-section-icon">
                        <svg viewBox="0 0 384 512" width="14" height="14" fill="currentColor" aria-hidden="true">
                          <path d="M256 0v128h128L256 0zM224 128L224 0H48C21.49 0 0 21.49 0 48v416C0 490.5 21.49 512 48 512h288c26.51 0 48-21.49 48-48V160h-127.1C238.3 160 224 145.7 224 128zM272 416h-160C103.2 416 96 408.8 96 400C96 391.2 103.2 384 112 384h160c8.836 0 16 7.162 16 16C288 408.8 280.8 416 272 416zM272 352h-160C103.2 352 96 344.8 96 336C96 327.2 103.2 320 112 320h160c8.836 0 16 7.162 16 16C288 344.8 280.8 352 272 352zM288 272C288 280.8 280.8 288 272 288h-160C103.2 288 96 280.8 96 272C96 263.2 103.2 256 112 256h160C280.8 256 288 263.2 288 272z" />
                        </svg>
                      </span>
                      Datos del expediente
                    </div>
                    <Row gutter={[12, 12]}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label={
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                              }}
                            >
                              <span>FECHA DE EMISIÓN DEL CITATORIO</span>
                            </span>
                          }
                          name="fecha_emision_expediente"
                          rules={rules.fecha_emision_expediente}
                        >
                          <div style={{ position: "relative", overflow: "hidden", width: "100%", display: "block" }}>
                            <Input
                              ref={fechaEmisionRef}
                              style={{ width: "100%" }}
                              placeholder="DD/MM/YYYY"
                              maxLength={10}
                              value={fechaEmisionValue || ""}
                              onChange={makeFechaInputHandler("fecha_emision_expediente")}
                              onKeyDown={makeEnterHandler(fechaEmisionRef)}
                              suffix={
                                <CalendarOutlined
                                  style={{ cursor: "pointer", color: "#1677ff" }}
                                  onClick={() => setOpenFechaEmisionPicker(true)}
                                />
                              }
                            />

                            <ScrollDatePicker
                              open={openFechaEmisionPicker}
                              onOpenChange={setOpenFechaEmisionPicker}
                              format="DD/MM/YYYY"
                              value={
                                fechaEmisionValue
                                  ? dayjs(fechaEmisionValue, "DD/MM/YYYY", true)
                                  : null
                              }
                              onChange={handleFechaEmisionCalendarChange}
                              style={{
                                position: "absolute",
                                inset: 0,
                                opacity: 0,
                                visibility: "hidden",
                                zIndex: -1,
                                pointerEvents: "none",
                                width: 0,
                                height: 0,
                              }}
                            />
                          </div>
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12}>
                        <Form.Item
                          label={
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                              }}
                            >
                              <span>RAZÓN SOCIAL</span>
                              <Text
                                style={{
                                  fontSize: 12,
                                  fontWeight: 400,
                                  textTransform: "none",
                                  letterSpacing: "normal",
                                  color: "#0A539A",
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                  userSelect: "none",
                                }}
                                onClick={() => setNuevaEmpresaModal(true)}
                              >
                                <PlusOutlined style={{ marginRight: 3 }} />
                                ¿Empresa nueva? Créala aquí
                              </Text>
                            </span>
                          }
                          name="empresa_razon_social_id"
                          rules={rules.empresa_razon_social_id}
                        >
                          <Select
                            ref={razonSocialRef}
                            placeholder="Selecciona Razón social"
                            options={allRazonesSociales}
                            showSearch
                            allowClear
                            mode="multiple"
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            getPopupContainer={(trigger) => trigger.parentNode}
                            onKeyDown={makeEnterHandler(razonSocialRef)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[12, 12]}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label="OBJETO DE LA SOLICITUD"
                          name="objeto_solicitud"
                        >
                          <Select
                            ref={objetoSolicitudRef}
                            placeholder="Selecciona el objeto de la solicitud"
                            options={[...objetosOptions]}
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            onKeyDown={makeEnterHandler(objetoSolicitudRef)}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12}>
                        <Form.Item
                          label="NÚM. IDENTIFICACIÓN ÚNICO"
                          name="exp"
                          rules={rules.exp}
                          normalize={makeNormalizer("exp")}
                        >
                          <Input
                            ref={expRef}
                            placeholder="Solo escribe el folio"
                            allowClear
                            maxLength={50}
                            onKeyDown={makeEnterHandler(expRef)}
                            addonBefore={
                              cityIdentificationCode ? (
                                <span
                                  style={{
                                    fontWeight: 700,
                                    color: "#0A539A",
                                    letterSpacing: "0.02em",
                                  }}
                                >
                                  {cityIdentificationCode}
                                </span>
                              ) : undefined
                            }
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[12, 12]}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label="FECHA Y HORA DE LA PRÓXIMA AUDIENCIA"
                          name="fecha_audiencia"
                          rules={rules.fecha_audiencia}
                        >
                          <div style={{ position: "relative", overflow: "hidden", width: "100%", display: "block" }}>
                            <Input
                              ref={fechaAudienciaRef}
                              style={{ width: "100%" }}
                              placeholder="DD/MM/YYYY HH:mm"
                              maxLength={16}
                              value={fechaAudienciaValue || ""}
                              onChange={makeFechaHoraInputHandler("fecha_audiencia")}
                              onKeyDown={makeEnterHandler(fechaAudienciaRef)}
                              suffix={
                                <CalendarOutlined
                                  style={{ cursor: "pointer", color: "#1677ff" }}
                                  onClick={() => setOpenFechaAudienciaPicker(true)}
                                />
                              }
                            />

                            <ScrollDatePicker
                              open={openFechaAudienciaPicker}
                              onOpenChange={setOpenFechaAudienciaPicker}
                              format="DD/MM/YYYY HH:mm"
                              showTime={{ format: "HH:mm" }}
                              value={
                                fechaAudienciaValue
                                  ? dayjs(fechaAudienciaValue, "DD/MM/YYYY HH:mm", true)
                                  : null
                              }
                              onChange={handleFechaAudienciaCalendarChange}
                              style={{
                                position: "absolute",
                                inset: 0,
                                opacity: 0,
                                visibility: "hidden",
                                zIndex: -1,
                                pointerEvents: "none",
                                width: 0,
                                height: 0,
                              }}
                            />
                          </div>
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12}>
                        <Form.Item
                          label="FORMA DE NOTIFICACIÓN"
                          name="forma_notificacion"
                          rules={rules.forma_notificacion}
                        >
                          <Select
                            ref={formaNotifRef}
                            placeholder="Selecciona"
                            showSearch
                            options={formaNotifOptions}
                            onChange={(v) => {
                              if (v !== "actuario")
                                form.setFieldsValue({ fecha_hora_cita_recepcion: null });
                              if (!v) {
                                form.setFieldsValue({
                                  origen_actuario: null,
                                  id_medio_notificacion: null,
                                  fecha_hora_cita_recepcion: null,
                                });
                              }
                            }}
                            onKeyDown={makeEnterHandler(formaNotifRef)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[12, 12]}>
                      {formaNotifSel && (
                        <Col xs={24} md={12}>
                          <Form.Item
                            label="A QUIEN SE LE NOTIFICÓ?"
                            name="origen_actuario"
                          >
                            <Select
                              ref={origenActuarioRef}
                              placeholder="Selecciona origen"
                              showSearch
                              options={origenNotifOptions}
                              onKeyDown={makeEnterHandler(origenActuarioRef)}
                            />
                          </Form.Item>
                        </Col>
                      )}

                      {formaNotifSel === "actuario" && (
                        <Col xs={24} md={12}>
                          <Form.Item
                            label="FECHA Y HORA DE RECEPCIÓN DEL CITATORIO"
                            name="fecha_hora_cita_recepcion"
                            rules={rules.fecha_hora_cita_recepcion}
                          >
                            <div style={{ position: "relative", overflow: "hidden", width: "100%", display: "block" }}>
                              <Input
                                ref={fechaRecepcionRef}
                                style={{ width: "100%" }}
                                placeholder="DD/MM/YYYY HH:mm"
                                maxLength={16}
                                value={fechaRecepcionValue || ""}
                                onChange={makeFechaHoraInputHandler("fecha_hora_cita_recepcion")}
                                onKeyDown={makeEnterHandler(fechaRecepcionRef)}
                                suffix={
                                  <CalendarOutlined
                                    style={{ cursor: "pointer", color: "#1677ff" }}
                                    onClick={() => setOpenFechaRecepcionPicker(true)}
                                  />
                                }
                              />

                              <ScrollDatePicker
                                open={openFechaRecepcionPicker}
                                onOpenChange={setOpenFechaRecepcionPicker}
                                format="DD/MM/YYYY HH:mm"
                                showTime={{ format: "HH:mm" }}
                                value={
                                  fechaRecepcionValue
                                    ? dayjs(fechaRecepcionValue, "DD/MM/YYYY HH:mm", true)
                                    : null
                                }
                                onChange={handleFechaRecepcionCalendarChange}
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  opacity: 0,
                                  visibility: "hidden",
                                  zIndex: -1,
                                  pointerEvents: "none",
                                  width: 0,
                                  height: 0,
                                }}
                              />
                            </div>
                          </Form.Item>
                        </Col>
                      )}

                      {formaNotifSel === "trabajador" && (
                        <Col xs={24} md={12}>
                          <Form.Item
                            label="MEDIO DE NOTIFICACIÓN"
                            name="id_medio_notificacion"
                            rules={rules.id_medio_notificacion}
                          >
                            <Select
                              ref={medioNotifRef}
                              placeholder="Selecciona medio"
                              showSearch
                              loading={loadingMedios}
                              options={medioNotifOptions}
                              optionFilterProp="label"
                              notFoundContent={
                                loadingMedios ? "Cargando..." : "No hay medios disponibles"
                              }
                              onKeyDown={makeEnterHandler(medioNotifRef)}
                            />
                          </Form.Item>
                        </Col>
                      )}
                    </Row>
                  </div>
                </div>

                {/* ===== Citatorio (ancho completo, abajo) ===== */}
                <div className="cc-section-card cc-full-width">
                  <div className="cc-section-header">
                    <span className="cc-section-icon">
                      <svg viewBox="0 0 384 512" width="14" height="14" fill="currentColor" aria-hidden="true">
                        <path d="M88 304H80V256H88C101.3 256 112 266.7 112 280C112 293.3 101.3 304 88 304zM192 256H200C208.8 256 216 263.2 216 272V336C216 344.8 208.8 352 200 352H192V256zM224 0V128C224 145.7 238.3 160 256 160H384V448C384 483.3 355.3 512 320 512H64C28.65 512 0 483.3 0 448V64C0 28.65 28.65 0 64 0H224zM64 224C55.16 224 48 231.2 48 240V368C48 376.8 55.16 384 64 384C72.84 384 80 376.8 80 368V336H88C118.9 336 144 310.9 144 280C144 249.1 118.9 224 88 224H64zM160 368C160 376.8 167.2 384 176 384H200C226.5 384 248 362.5 248 336V272C248 245.5 226.5 224 200 224H176C167.2 224 160 231.2 160 240V368zM288 224C279.2 224 272 231.2 272 240V368C272 376.8 279.2 384 288 384C296.8 384 304 376.8 304 368V320H336C344.8 320 352 312.8 352 304C352 295.2 344.8 288 336 288H304V256H336C344.8 256 352 248.8 352 240C352 231.2 344.8 224 336 224H288zM256 0L384 128H256V0z" />
                      </svg>
                    </span>
                    Citatorio
                  </div>
                  <DocBlock keyName="citatorio" />
                </div>
              </Form>
            </Spin>
        </div>

        <style>
          {`
.expediente-form-wrapper {
  width: 100%;
  margin: 0 auto;
}

/* ── Header de la página ── */
.cc-page-header-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px 24px;
  box-shadow: 0 2px 10px rgba(15, 34, 58, 0.06);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.expediente-form-header-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.expediente-form-title {
  font-size: 28px;
  font-weight: 650;
  color: #0f172a;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.expediente-form-subtitle {
  color: #7a7f87;
  font-size: 13px;
}

.expediente-btn-clean {
  min-height: 40px;
  border-radius: 12px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-weight: 700;
}

.expediente-btn-clean:hover {
  background: #e2e8f0 !important;
  border-color: #cbd5e1 !important;
  color: #334155 !important;
}

.expediente-btn-save {
  min-height: 40px;
  border-radius: 12px;
}

.cc-section-card .ant-btn,
.expediente-doc-block .ant-btn {
  min-height: 40px;
  border-radius: 12px;
}

/* ── Tarjetas de sección ── */
.cc-section-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px 24px 20px;
  box-shadow: 0 2px 8px rgba(15, 34, 58, 0.05);
  margin-bottom: 24px;
}

.cc-full-width {
  margin-bottom: 24px;
}

.cc-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.09em;
  color: #111827;
  text-transform: uppercase;
}

.cc-section-icon {
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 0;
  background: #DCE9F5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #0A539A;
}

.cc-count-badge {
  margin-left: 6px;
  background: #0A539A;
  color: #fff;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 9px;
  min-width: 24px;
  text-align: center;
  text-transform: none;
  letter-spacing: normal;
}

/* ── Layout de dos columnas ── */
.cc-body-grid {
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 24px;
  align-items: start;
  margin-bottom: 24px;
}

.cc-body-grid .cc-section-card {
  margin-bottom: 0;
}

@media (max-width: 1100px) {
  .cc-body-grid {
    grid-template-columns: 320px 1fr;
  }
}

@media (max-width: 768px) {
  .cc-body-grid {
    grid-template-columns: 1fr;
  }
  .cc-body-grid .cc-section-card {
    height: auto;
  }
}

/* ── Inputs dentro de las tarjetas ── */
.expediente-ant-form .ant-form-item {
  margin-bottom: 8px;
}


.expediente-ant-form .ant-form-item-label > label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #6b7280;
  text-transform: uppercase;
  width: 100%;
}

.cc-section-card .ant-input,
.cc-section-card .ant-input-affix-wrapper,
.cc-section-card .ant-select-selector,
.cc-section-card .ant-picker {
  background: #f8fafc !important;
  border-color: #e2e8f0 !important;
  box-shadow: none !important;
  height: 40px !important;
  min-height: 40px !important;
  border-radius: 12px !important;
}

.cc-section-card .ant-input-affix-wrapper > .ant-input {
  height: 100% !important;
  min-height: 0 !important;
}

.cc-section-card .ant-select-single .ant-select-selector,
.cc-section-card .ant-select-multiple .ant-select-selector {
  display: flex;
  align-items: center;
}

.cc-section-card .ant-select-multiple .ant-select-selector {
  height: auto !important;
  min-height: 40px !important;
}

.cc-section-card .ant-input:focus,
.cc-section-card .ant-input-affix-wrapper-focused,
.cc-section-card .ant-select-focused .ant-select-selector,
.cc-section-card .ant-picker-focused {
  border-color: #0A539A !important;
  box-shadow: 0 0 0 3px rgba(10, 83, 154, 0.12) !important;
}

.trabajadores-scroll {
  max-height: 360px;
  overflow-y: auto;
  padding-right: 4px;
}

.expediente-doc-block .ant-upload-drag {
  background: #f8fafc;
  border-color: #e2e8f0;
}

@media (max-width: 992px) {
  .cc-page-header-card {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 768px) {
  .cc-section-card {
    padding: 16px 16px 4px;
  }
}
          `}
        </style>

        {/* Modal: Nueva empresa / razón social */}
        <Modal
          open={nuevaEmpresaModal}
          title="Nueva empresa"
          footer={null}
          onCancel={() => setNuevaEmpresaModal(false)}
          width={720}
          destroyOnClose
          centered
        >
          <Spin spinning={savingNuevaEmpresa} tip="Guardando...">
            <FormEmpresas
              ref={nuevaEmpresaRef}
              isEdit={false}
              initialValues={{}}
              onCancel={() => setNuevaEmpresaModal(false)}
              onSaved={() => setNuevaEmpresaModal(false)}
              onSubmit={handleGuardarNuevaEmpresa}
            />
          </Spin>
        </Modal>

        <SuccessOverlay
          show={success.show}
          title={success.title}
          subtitle={success.subtitle}
          onDone={handleSuccessDone}
        />
      </>
      </ConfigProvider>
    );
  }
);

export default FormCentroConciliacion;
